import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, User, ArrowRight, Clock, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import blogService, { PublicBlog } from '../../services/user/blogService';
import { getImageUrl } from '../../utils/imageHelper';

const Blog: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuredRef, featuredVisible] = useScrollAnimation();
  const [postsRef, postsVisible] = useScrollAnimation();
  const [learnMoreExpanded, setLearnMoreExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [blogPosts, setBlogPosts] = useState<PublicBlog[]>([]);
  const [loading, setLoading] = useState(true);
  const postsPerPage = 6;

  // Helper to check if a post is featured (handles both boolean and number)
  const isFeatured = (post: PublicBlog) => {
    if (post.featured === true || post.featured === 1 || post.featured === '1') return true;
    if (post.featured === false || post.featured === 0 || post.featured === '0') return false;
    return Boolean(post.featured);
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const data = await blogService.getPublishedBlogs({
        page: 1,
        limit: 100, // Fetch enough for pagination
      });

      if (data && data.blogs && Array.isArray(data.blogs)) {
        setBlogPosts(data.blogs);
      } else {
        setBlogPosts([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch blogs:', error);
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  // Removed mock data - using real API data only
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const unusedMockBlogPosts = [
    {
      id: 1,
      title: 'The Future of QR Payments: Trends and Predictions',
      excerpt: 'Explore how QR payment technology is evolving and what trends are shaping the future of digital transactions.',
      author: 'Rita Team',
      date: 'March 15, 2024',
      readTime: '5 min read',
      category: 'Technology',
      image: '📱'
    },
    {
      id: 2,
      title: 'How to Boost Your Business with QR Payment Solutions',
      excerpt: 'Learn practical strategies to increase sales and improve customer experience using QR payment technology.',
      author: 'Business Team',
      date: 'March 10, 2024',
      readTime: '7 min read',
      category: 'Business',
      image: '💼'
    },
    {
      id: 3,
      title: 'Security Best Practices for Digital Wallets',
      excerpt: 'Essential security tips to protect your digital wallet and ensure safe transactions in the digital age.',
      author: 'Security Team',
      date: 'March 5, 2024',
      readTime: '6 min read',
      category: 'Security',
      image: '🔒'
    },
    {
      id: 4,
      title: 'Understanding Business Analytics: A Complete Guide',
      excerpt: 'Discover how to leverage analytics to make data-driven decisions and grow your business effectively.',
      author: 'Analytics Team',
      date: 'February 28, 2024',
      readTime: '8 min read',
      category: 'Analytics',
      image: '📊'
    },
    {
      id: 5,
      title: 'Mobile App vs Web Platform: Which is Right for You?',
      excerpt: 'Compare the benefits of mobile apps and web platforms to choose the best solution for your business needs.',
      author: 'Product Team',
      date: 'February 20, 2024',
      readTime: '5 min read',
      category: 'Product',
      image: '🌐'
    },
    {
      id: 6,
      title: 'Customer Success Stories: How Rita Transformed Businesses',
      excerpt: 'Real stories from businesses that have successfully integrated Rita and seen remarkable growth.',
      author: 'Marketing Team',
      date: 'February 15, 2024',
      readTime: '6 min read',
      category: 'Success Stories',
      image: '⭐'
    },
    {
      id: 7,
      title: 'The Rise of Contactless Payments: A Post-Pandemic Analysis',
      excerpt: 'Understanding how contactless payment methods have evolved and become essential in the modern economy.',
      author: 'Research Team',
      date: 'February 10, 2024',
      readTime: '7 min read',
      category: 'Technology',
      image: '📲'
    },
    {
      id: 8,
      title: 'Maximizing Revenue with QR Payment Integration',
      excerpt: 'Discover how integrating QR payments can significantly boost your business revenue and customer satisfaction.',
      author: 'Business Team',
      date: 'February 5, 2024',
      readTime: '6 min read',
      category: 'Business',
      image: '💰'
    },
    {
      id: 9,
      title: 'Protecting Your Business from Payment Fraud',
      excerpt: 'Essential security measures and best practices to protect your business from payment fraud and cyber threats.',
      author: 'Security Team',
      date: 'January 30, 2024',
      readTime: '8 min read',
      category: 'Security',
      image: '🛡️'
    },
    {
      id: 10,
      title: 'Data-Driven Decision Making: Analytics for Small Businesses',
      excerpt: 'Learn how small businesses can leverage analytics to make informed decisions and drive growth.',
      author: 'Analytics Team',
      date: 'January 25, 2024',
      readTime: '6 min read',
      category: 'Analytics',
      image: '📈'
    },
    {
      id: 11,
      title: 'Building a Mobile-First Payment Strategy',
      excerpt: 'Why mobile-first approaches are crucial for modern payment systems and how to implement them effectively.',
      author: 'Product Team',
      date: 'January 20, 2024',
      readTime: '5 min read',
      category: 'Product',
      image: '📱'
    },
    {
      id: 12,
      title: 'From Startup to Success: Rita Customer Journey',
      excerpt: 'Inspiring stories of startups that scaled their business using Rita payment solutions.',
      author: 'Marketing Team',
      date: 'January 15, 2024',
      readTime: '7 min read',
      category: 'Success Stories',
      image: '🚀'
    },
    {
      id: 13,
      title: 'Blockchain and QR Payments: The Future of Transactions',
      excerpt: 'Exploring how blockchain technology is revolutionizing QR payment systems and transaction security.',
      author: 'Technology Team',
      date: 'January 10, 2024',
      readTime: '9 min read',
      category: 'Technology',
      image: '⛓️'
    },
    {
      id: 14,
      title: 'Customer Retention Strategies for Digital Payment Platforms',
      excerpt: 'Effective strategies to retain customers and build loyalty in the competitive digital payment landscape.',
      author: 'Business Team',
      date: 'January 5, 2024',
      readTime: '6 min read',
      category: 'Business',
      image: '👥'
    },
    {
      id: 15,
      title: 'Multi-Factor Authentication in Digital Wallets',
      excerpt: 'Understanding the importance of multi-factor authentication and how it enhances wallet security.',
      author: 'Security Team',
      date: 'December 30, 2023',
      readTime: '7 min read',
      category: 'Security',
      image: '🔐'
    },
    {
      id: 16,
      title: 'Predictive Analytics: Forecasting Business Trends',
      excerpt: 'How predictive analytics can help businesses forecast trends and make proactive decisions.',
      author: 'Analytics Team',
      date: 'December 25, 2023',
      readTime: '8 min read',
      category: 'Analytics',
      image: '🔮'
    },
    {
      id: 17,
      title: 'User Experience Design in Payment Applications',
      excerpt: 'Best practices for designing intuitive and user-friendly payment application interfaces.',
      author: 'Product Team',
      date: 'December 20, 2023',
      readTime: '6 min read',
      category: 'Product',
      image: '🎨'
    },
    {
      id: 18,
      title: 'Case Study: Restaurant Chain Increases Sales by 40%',
      excerpt: 'A detailed case study of how a restaurant chain used Rita to increase sales and improve customer experience.',
      author: 'Marketing Team',
      date: 'December 15, 2023',
      readTime: '7 min read',
      category: 'Success Stories',
      image: '🍽️'
    }
  ];

  // Extract unique categories from blogs
  const categories = useMemo(() => {
    const cats = ['All'];
    const uniqueCats = Array.from(new Set(blogPosts.map(blog => blog.category))).filter(Boolean);
    return [...cats, ...uniqueCats.sort()];
  }, [blogPosts]);

  // Fetch categories from API
  const [allCategories, setAllCategories] = useState<string[]>(['All']);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await blogService.getBlogCategories();
        if (cats && cats.length > 0) {
          setAllCategories(['All', ...cats.sort()]);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        // Fallback to categories extracted from blogs
      }
    };
    fetchCategories();
  }, []);

  // Filter posts by category
  const filteredPosts = useMemo(() => {
    if (selectedCategory === 'All') {
      return blogPosts;
    }
    return blogPosts.filter(post => post.category === selectedCategory);
  }, [selectedCategory, blogPosts]);

  // Get featured post (first featured blog, or first post if no featured)
  const featuredPost = useMemo(() => {
    if (filteredPosts.length === 0) return null;

    const featured = filteredPosts.find(post => isFeatured(post));

    // Show featured post if found, otherwise show first post
    return featured || filteredPosts[0];
  }, [filteredPosts]);

  // Sort posts: featured first, then by date (newest first)
  const sortedPosts = useMemo(() => {
    return [...filteredPosts].sort((a, b) => {
      // Featured posts first
      const aFeatured = isFeatured(a);
      const bFeatured = isFeatured(b);
      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;
      // Then by date (newest first)
      return new Date(b.published_at || b.created_at).getTime() -
        new Date(a.published_at || a.created_at).getTime();
    });
  }, [filteredPosts]);

  // Get posts for pagination (include all posts, but exclude the featured post from the grid if shown above)
  const postsForGrid = useMemo(() => {
    // If we're showing a featured post section, exclude it from the grid
    if (featuredPost) {
      return sortedPosts.filter(post => post.id !== featuredPost.id);
    }
    return sortedPosts;
  }, [sortedPosts, featuredPost]);

  // Get posts for current page
  const paginatedPosts = useMemo(() => {
    const startIndex = (currentPage - 1) * postsPerPage;
    const endIndex = startIndex + postsPerPage;
    return postsForGrid.slice(startIndex, endIndex);
  }, [postsForGrid, currentPage, postsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(postsForGrid.length / postsPerPage);

  // Handle category change
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page when category changes
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#faf8ed]">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 md:pt-52 pb-20 md:pb-32 px-4 bg-mesh-premium relative overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#0c513f]/5 rounded-full blur-[100px] animate-blob"></div>
          <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-chowdeck-pastelPurple/20 rounded-full blur-[120px] animate-blob animation-delay-3000"></div>
        </div>

        <div className={`container-premium text-center px-4 md:px-6 transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
          }`}>
          <h1 className="heading-huge text-[#0c513f] mb-8 tracking-tighter-premium">
            Our Insights
          </h1>
          <p className="text-xl md:text-3xl text-secondary-900 max-w-3xl mx-auto leading-tight font-medium tracking-tight">
            Stay updated with the latest trends in QR payments, digital wallets, and business growth.
          </p>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 1 && (
        <section className="py-4 px-4 border-b border-[#0c513f]/5 bg-white/50 backdrop-blur-md sticky top-[72px] md:top-[88px] z-30">
          <div className="container-premium overflow-hidden">
            <div className="flex flex-nowrap md:flex-wrap overflow-x-auto no-scrollbar gap-3 md:gap-4 justify-start md:justify-center pb-2 md:pb-0 px-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-5 md:px-6 py-2 md:py-2.5 rounded-full font-bold text-sm md:text-base transition-all duration-300 whitespace-nowrap ${selectedCategory === category
                    ? 'bg-[#0c513f] text-[#faf8ed] shadow-lg scale-105'
                    : 'bg-white border-2 border-secondary-100 text-secondary-600 hover:border-[#0c513f] hover:text-[#0c513f]'
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Post */}
      {featuredPost && (
        <section ref={featuredRef} className="py-20 px-4 bg-white rounded-chow-lg -mt-10 relative z-20">
          <div className="container-premium">
            <Link
              to={`/blog/${featuredPost.id}`}
              className="group bg-[#faf8ed] rounded-[3rem] overflow-hidden shadow-sm hover:shadow-2xl border-2 border-transparent hover:border-[#0c513f]/10 transition-all duration-700 block relative"
            >
              <div className="md:flex items-stretch">
                <div className="md:w-1/2 bg-[#0c513f] flex items-center justify-center h-72 md:h-[500px] overflow-hidden relative">
                  <div className="absolute inset-0 bg-grid-premium opacity-10"></div>
                  {featuredPost.image ? (
                    <img
                      src={getImageUrl(featuredPost.image) || featuredPost.image}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 opacity-90 group-hover:opacity-100"
                    />
                  ) : (
                    <div className="text-9xl grayscale opacity-20">📝</div>
                  )}
                </div>
                <div className="md:w-1/2 p-8 md:p-16 flex flex-col justify-center">
                  <div className="flex items-center space-x-4 mb-6 md:mb-8">
                    <span className="bg-[#0c513f] text-[#faf8ed] px-4 py-1.5 rounded-full text-xs md:sm font-bold uppercase tracking-wider">
                      Featured
                    </span>
                    <span className="text-secondary-600 text-xs md:sm font-bold bg-white px-3 py-1.5 rounded-full border border-secondary-100">{featuredPost.category}</span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-black text-[#0c513f] mb-4 md:mb-6 leading-tight tracking-tight group-hover:text-[#0a4535] transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-lg md:text-xl text-secondary-600 mb-6 md:mb-10 leading-relaxed font-medium line-clamp-3">
                    {featuredPost.description}
                  </p>

                  <div className="flex items-center justify-between mt-auto pt-8 border-t border-[#0c513f]/10">
                    <div className="flex items-center space-x-6 text-secondary-500 font-bold text-sm">
                      <div className="flex items-center space-x-2">
                        <User size={16} />
                        <span>{featuredPost.author}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock size={16} />
                        <span>{featuredPost.read_time} min read</span>
                      </div>
                    </div>
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[#0c513f] shadow-md group-hover:bg-[#0c513f] group-hover:text-white transition-all duration-300 transform group-hover:scale-110">
                      <ArrowRight size={20} className="-rotate-45 group-hover:rotate-0 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section ref={postsRef} className="py-20 px-4">
        <div className="container-premium max-w-8xl">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="animate-spin text-[#0c513f]" size={48} />
            </div>
          ) : paginatedPosts.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 mb-20">
                {paginatedPosts.map((post, index) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.id}`}
                    className={`bg-white rounded-[2.5rem] p-4 border-2 border-transparent hover:border-[#0c513f]/10 shadow-sm hover:shadow-2xl transition-all duration-500 group hover:scale-[1.02] hover:-translate-y-2 block ${postsVisible
                      ? `animate-fade-in-up opacity-100`
                      : 'opacity-0'
                      }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="bg-[#faf8ed] rounded-[2rem] h-64 overflow-hidden mb-6 relative">
                      {post.image ? (
                        <img
                          src={getImageUrl(post.image) || post.image}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-7xl opacity-20">📝</div>
                      )}

                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur text-[#0c513f] px-4 py-1.5 rounded-full text-xs font-bold uppercase shadow-sm">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="px-2 pb-4">
                      <div className="flex items-center space-x-3 mb-4 text-xs font-bold text-secondary-500">
                        <span className="flex items-center bg-secondary-50 px-3 py-1 rounded-full"><Clock size={12} className="mr-1" /> {post.read_time} min</span>
                        <span className="flex items-center bg-secondary-50 px-3 py-1 rounded-full"><Calendar size={12} className="mr-1" /> {new Date(post.published_at || post.created_at).toLocaleDateString()}</span>
                      </div>

                      <h3 className="text-2xl font-black text-[#0c513f] mb-4 group-hover:text-[#0a4535] transition-colors leading-tight">
                        {post.title}
                      </h3>
                      <p className="text-secondary-600 mb-6 line-clamp-3 font-medium text-lg leading-relaxed">
                        {post.description}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-secondary-100">
                        <div className="flex items-center space-x-2 text-sm font-bold text-secondary-600">
                          <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                            <User size={14} />
                          </div>
                          <span>{post.author}</span>
                        </div>
                        <div className="text-[#0c513f] font-bold flex items-center group-hover:translate-x-1 transition-transform">
                          Read <ArrowRight size={16} className="ml-1" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-12">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-14 h-14 rounded-full border-2 border-secondary-200 flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0c513f] hover:text-white hover:border-[#0c513f]"
                  >
                    <ChevronLeft size={24} />
                  </button>

                  <div className="flex items-center gap-3 bg-white p-2 rounded-full shadow-sm">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`w-12 h-12 rounded-full font-black text-lg transition-all ${currentPage === page
                              ? 'bg-[#0c513f] text-white shadow-lg scale-110'
                              : 'text-secondary-500 hover:bg-secondary-100'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return (
                          <span key={page} className="w-10 flex justify-center text-secondary-400">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-14 h-14 rounded-full border-2 border-secondary-200 flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0c513f] hover:text-white hover:border-[#0c513f]"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-2xl text-secondary-600 font-bold">
                No posts found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Learn More About Rita - SEO Section */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setLearnMoreExpanded(!learnMoreExpanded)}
              className="bg-secondary-200 hover:bg-secondary-300 text-secondary-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2 shadow-sm hover:shadow-md"
            >
              <span>Learn More About Rita</span>
              {learnMoreExpanded ? (
                <ChevronUp size={20} className="transition-transform" />
              ) : (
                <ChevronDown size={20} className="transition-transform" />
              )}
            </button>
          </div>

          <div
            className={`overflow-hidden transition-all duration-500 ${learnMoreExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <div className="max-w-4xl mx-auto space-y-8 text-secondary-700">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
                  Rita - A Universal QR Payment & Discovery Platform
                </h2>
                <div className="space-y-4 text-lg leading-relaxed">
                  <p>
                    Rita is a comprehensive digital platform that revolutionizes the way customers discover businesses and make payments.
                    Our universal QR payment and discovery platform offers seamless digital wallet solutions, instant QR code payments,
                    business location services, and real-time navigation. Whether you're looking for nearby stores, restaurants, salons,
                    fuel stations, or service providers, Rita simplifies your journey by combining business discovery, navigation, and
                    payment processing into one intuitive platform.
                  </p>
                  <p>
                    Our mission is to empower businesses of all sizes while providing customers with a smooth, convenient experience.
                    For consumers, Rita offers a seamless way to discover and pay at local businesses without the need for cash or cards.
                    For business owners, we provide cutting-edge point-of-sale systems, comprehensive analytics, and intelligent payment
                    solutions that help grow their customer base and streamline operations. Rita delivers convenience, transparency, and
                    cutting-edge technology through our advanced business management tools, dedicated mobile applications, and secure
                    payment infrastructure.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Key Features of Rita
                </h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Digital Wallet
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita's secure and intuitive digital wallet allows users to hold funds, make instant payments, and track all
                      transactions in real-time. With our wallet, you can easily manage your finances, view transaction history,
                      and enjoy the convenience of scanning QR codes instead of carrying cash or cards. The wallet is protected
                      by advanced encryption and security measures to ensure your money and personal information remain safe.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      QR Code Payment System
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Our innovative QR code payment system enables instant, contactless transactions at any business partner.
                      Simply scan the QR code displayed at the business location, confirm the amount, and complete your payment
                      securely through your digital wallet. This eliminates the need for physical cash or credit cards, making
                      transactions faster, safer, and more convenient for both customers and businesses.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Business Discovery & Navigation
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita's powerful discovery platform helps you find nearby businesses based on your location, preferences,
                      and search criteria. Our integrated mapping system provides real-time navigation, directions, and business
                      information including hours, ratings, and available services. Whether you're looking for a specific store,
                      restaurant, or service provider, Rita makes it easy to discover and navigate to your destination.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Business Analytics & Management
                    </h4>
                    <p className="text-lg leading-relaxed">
                      For business owners, Rita offers comprehensive analytics and management tools to track performance, monitor
                      transactions, and gain valuable insights into customer behavior. Our dashboard provides real-time reports,
                      sales analytics, customer demographics, and performance metrics to help you make data-driven decisions and
                      grow your business effectively.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Security & Verification
                    </h4>
                    <p className="text-lg leading-relaxed">
                      All businesses on the Rita platform undergo KYC (Know Your Customer) verification to ensure authenticity
                      and security. Our platform uses industry-standard encryption protocols to protect all transactions and
                      personal data. With Rita, you can trust that every transaction is secure, verified, and protected by
                      advanced security measures.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Multi-Category Support
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita supports businesses across multiple categories including retail stores, restaurants, cafes, salons,
                      fuel stations, service providers, healthcare facilities, and more. Our platform is designed to accommodate
                      various business types and industries, making it a versatile solution for businesses of all sizes and sectors.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  How Rita Benefits You
                </h3>
                <div className="space-y-4 text-lg leading-relaxed">
                  <p>
                    <strong className="text-secondary-900">For Customers:</strong> Rita provides a seamless, cashless payment
                    experience with the convenience of discovering and navigating to nearby businesses. Our platform saves time,
                    reduces the need for physical currency, and offers a secure way to manage your transactions and explore local
                    businesses.
                  </p>
                  <p>
                    <strong className="text-secondary-900">For Businesses:</strong> Rita helps businesses reach more customers,
                    streamline payment processing, and gain valuable insights through our analytics platform. Our tools enable
                    businesses to accept digital payments easily, track performance, and grow their customer base with minimal
                    setup and maintenance.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Getting Started with Rita
                </h3>
                <p className="text-lg leading-relaxed">
                  Getting started with Rita is simple and straightforward. Download our mobile application, create your account,
                  and set up your digital wallet. For businesses, our onboarding process includes KYC verification, business profile
                  setup, and QR code generation. Our support team is available to assist you throughout the process, ensuring a
                  smooth and hassle-free experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;

